import React, {useState} from 'react';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';

import {v2Api} from '@/lib/api';
import {toast} from 'sonner';
import type {IActiveScenarioDetailParticipantsGrouped} from '@/types/scenarios';

// explicit type for inject entries (mirror from scenarios.ts)
type TTXInject = IActiveScenarioDetailParticipantsGrouped['ttx'][number]['injects'] extends | Array<infer T>
    | undefined
    ? T
    : never;

/** API may send `inject_type` as a string (code) or nested `{ id, name, code, ... }`. */
function injectTypeDisplayLabel(inj: {
    inject_type_name?: string;
    inject_type?: string | { name?: string; code?: string } | null;
}): string {
    if (inj.inject_type_name) return inj.inject_type_name;
    const t = inj.inject_type;
    if (t == null) return '—';
    if (typeof t === 'string') return t;
    if (typeof t === 'object' && (t.name || t.code)) {
        return t.name || t.code || '—';
    }
    return '—';
}

interface ParticipantInjectsContentProps {
    injects: TTXInject[];
    participantName?: string;
    activeScenarioId?: string;
    onAfterEvaluate?: () => void;
}

export function ParticipantInjectsContent({
                                              injects,
                                              participantName,
                                              activeScenarioId,
                                              onAfterEvaluate,
                                          }: ParticipantInjectsContentProps) {
    const [selected, setSelected] = useState<TTXInject | null>(null);
    const [score, setScore] = useState<string>('');
    const [bonus, setBonus] = useState<string>('');
    const [remarks, setRemarks] = useState<string>('');
    const [saving, setSaving] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    const [statusFilter, setStatusFilter] = useState<'all' | 'with_response' | 'not_answered' | 'answered_pending' | 'scored'>('all');

    // keep local copy so we can update without reloading parent
    const [localInjects, setLocalInjects] = useState<TTXInject[]>(injects);
    React.useEffect(() => {
        setLocalInjects(injects);
    }, [injects]);

    if (!injects.length) {
        return (
            <p className="text-center py-4 text-muted-foreground">
                No injects have been sent to this participant yet.
            </p>
        );
    }

    const visibleInjects = localInjects.filter((inj) => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'with_response') {
            const hasText = Boolean(inj.response_text && inj.response_text.trim());
            const hasOptions = Array.isArray(inj.selected_option_ids) && inj.selected_option_ids.length > 0;
            return hasText || hasOptions;
        }
        return inj.status === statusFilter;
    });

    const openReview = (inj: TTXInject, options?: { readOnly?: boolean }) => {
        setSelected(inj);
        setReadOnly(Boolean(options?.readOnly));
        setScore(
            typeof inj.obtained_score === 'number' && inj.obtained_score >= 0
                ? String(inj.obtained_score)
                : '',
        );
        setBonus(
            typeof inj.bonus_points === 'number' && inj.bonus_points >= 0
                ? String(inj.bonus_points)
                : '',
        );
        setRemarks(inj.remarks ?? '');
    };

    const closeReview = () => {
        setSelected(null);
        setReadOnly(false);
        setSaving(false);
    };

    const handleSave = async () => {
        if (!selected || !activeScenarioId) return;
        setSaving(true);
        try {
            const payload: { obtained_score: number; bonus_points?: number; remarks?: string } = {
                obtained_score: 0,
            };
            const s = parseInt(score, 10);
            const b = parseInt(bonus, 10);
            if (Number.isNaN(s) || s < 0) {
                toast.error('Please enter a valid obtained score.');
                setSaving(false);
                return;
            }
            payload.obtained_score = s;
            if (!Number.isNaN(b)) payload.bonus_points = b;
            payload.remarks = remarks.trim();

            await v2Api.dashboard.scenarios.actives.markRankInjectDelivery(
                activeScenarioId,
                selected.id,
                payload,
            );
            toast.success('Inject evaluation saved.');
            // update local copy
            setLocalInjects((prev) =>
                prev.map((i) =>
                    i.id === selected.id
                        ? {
                            ...i,
                            obtained_score: payload.obtained_score ?? i.obtained_score,
                            bonus_points: payload.bonus_points ?? i.bonus_points,
                            remarks: payload.remarks ?? i.remarks,
                            is_evaluated: true,
                            status: 'scored',
                        }
                        : i,
                ),
            );
            if (onAfterEvaluate) {
                onAfterEvaluate();
            }
            closeReview();
        } catch (err: any) {
            const detail = err?.response?.data?.detail ?? 'Failed to save evaluation.';
            toast.error(detail);
            setSaving(false);
        }
    };

    return (
        <div className="space-y-3">
            {participantName && (
                <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-card-foreground">
                        Injects for <span className="font-semibold">{participantName}</span>
                    </p>
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] text-muted-foreground">Filter:</span>
                        <select
                            className="h-7 rounded-md border border-input bg-background text-xs px-2 py-1"
                            value={statusFilter}
                            onChange={(e) =>
                                setStatusFilter(e.target.value as typeof statusFilter)
                            }
                        >
                            <option value="all">All</option>
                            <option value="with_response">With response</option>
                            <option value="answered_pending">Pending review</option>
                            <option value="scored">Scored</option>
                            <option value="not_answered">Not answered</option>
                        </select>
                    </div>
                </div>
            )}
            <div className="space-y-3">
                {visibleInjects.map((inj, index) => {
                    const isManual = inj.points_type === 'manual';
                    // Show review/edit button only for manual injects:
                    // - "Review & Score" when answered but not yet evaluated
                    const canReview = isManual && inj.status === 'answered_pending' && !!activeScenarioId;

                    // determine status for label and bg
                    let statusLabel = 'Not answered';
                    let bgClass = 'bg-destructive/10 dark:bg-destructive/900 border-destructive/30 dark:border-destructive/700';
                    if (inj.status === 'scored' || inj.is_evaluated) {
                        statusLabel = 'Scored';
                        bgClass = 'bg-green-50 dark:bg-green-900 border-green-200 dark:border-green-700';
                    } else if (inj.status === 'answered_pending') {
                        statusLabel = isManual ? 'Pending review' : 'Answered (pending review)';
                        bgClass = 'bg-yellow-50 dark:bg-yellow-900 border-yellow-200 dark:border-yellow-700';
                    }
                    const answered = bgClass.includes('green');

                    return (
                        <div
                            key={inj.id}
                            className={`p-3 border rounded-lg space-y-2 ${bgClass}`}
                        >
                            <div className="flex justify-between items-start">
                                <span className="text-sm font-medium truncate">
                                  {index + 1}. {inj.title || `Inject #${inj.inject_id}`}
                                </span>
                                <Badge
                                    variant={answered ? 'secondary' : 'destructive'}
                                    className="text-[10px] uppercase tracking-wide"
                                >
                                    {statusLabel}
                                </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground flex flex-wrap gap-2">
                                <span>
                                  Inject type: {injectTypeDisplayLabel(inj)}
                                </span>
                                <span>
                                  Scoring: {inj.points_type === 'manual' ? 'Manual' : inj.points_type === 'decay' ? 'Decay' : 'Standard'}
                                </span>
                                <span>
                                  Points: {inj.points ?? 0}
                                    {typeof inj.bonus_points === 'number' && inj.bonus_points > 0 && (
                                        <> (+{inj.bonus_points})</>
                                    )}
                                </span>
                                {typeof inj.obtained_score === 'number' && (
                                    <span>Awarded: {inj.obtained_score}</span>
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                {inj.sent_at && <>Sent: {new Date(inj.sent_at).toLocaleString()}</>}
                            </div>
                            <div className="text-xs text-muted-foreground space-y-1">
                                {inj.response_text && (
                                    <code className="bg-muted px-2 py-0.5 rounded break-all block">
                                        {inj.response_text}
                                    </code>
                                )}
                                {!inj.response_text && inj.selected_options && inj.selected_options.length > 0 && (
                                    <p>
                                        Selected:{' '}
                                        <code className="bg-muted px-2 py-0.5 rounded break-all">
                                            {inj.selected_options.map((o) => o.text).join(', ')}
                                        </code>
                                    </p>
                                )}
                                {inj.remarks && (
                                    <p className="italic">
                                        Remarks: {inj.remarks}
                                    </p>
                                )}
                            </div>
                            <div className="pt-1 flex flex-wrap gap-2">
                                {canReview && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-[11px]"
                                        onClick={() =>
                                            openReview(inj, {
                                                readOnly: !canReview,
                                            })
                                        }
                                    >
                                        Review &amp; Score
                                    </Button>
                                )}
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-[11px]"
                                    onClick={() =>
                                        openReview(inj, {
                                            readOnly: true,
                                        })
                                    }
                                >
                                    View Details
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <Dialog open={!!selected} onOpenChange={(open) => !open && closeReview()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Inject Details &amp; Review</DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-3">
                            <div className="text-sm font-medium">
                                {selected.title || `Inject #${selected.inject_id}`}
                            </div>
                            <div className="text-xs text-muted-foreground flex flex-wrap gap-2">
                <span>
                  Inject type: {injectTypeDisplayLabel(selected)}
                </span>
                                <span>
                  Scoring: {selected.points_type === 'manual' ? 'Manual' : selected.points_type === 'decay' ? 'Decay' : 'Standard'}
                </span>
                                <span>Points: {selected.points ?? 0}</span>
                            </div>
                            {selected.response_text && (
                                <div className="text-xs text-muted-foreground">
                                    <div className="font-semibold mb-1">Participant response</div>
                                    <code className="bg-muted px-2 py-1 rounded block break-all">
                                        {selected.response_text}
                                    </code>
                                </div>
                            )}
                            {!selected.response_text && selected.selected_options && selected.selected_options.length > 0 && (
                                <div className="text-xs text-muted-foreground">
                                    <div className="font-semibold mb-1">Participant response</div>
                                    <code className="bg-muted px-2 py-1 rounded block break-all">
                                        {selected.selected_options.map((o) => o.text).join(', ')}
                                    </code>
                                </div>
                            )}
                            {selected.options && selected.options.length > 0 && (
                                <div className="text-xs text-muted-foreground">
                                    <div className="font-semibold mb-1">Available options</div>
                                    <ul className="list-disc pl-4 space-y-1">
                                        {selected.options
                                            .slice()
                                            .sort((a, b) => a.order - b.order)
                                            .map((o) => {
                                                const isSelected = selected.selected_option_ids?.includes(o.id);
                                                return (
                                                    <li
                                                        key={o.id}
                                                        className={isSelected ? 'font-semibold text-foreground' : ''}
                                                    >
                                                        {o.text}
                                                        {isSelected && ' (selected)'}
                                                    </li>
                                                );
                                            })}
                                    </ul>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <Label htmlFor="eval-score">Awarded points</Label>
                                    <Input
                                        id="eval-score"
                                        type="number"
                                        min={0}
                                        value={score}
                                        onChange={(e) => setScore(e.target.value)}
                                        disabled={readOnly}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="eval-bonus">Bonus points</Label>
                                    <Input
                                        id="eval-bonus"
                                        type="number"
                                        min={0}
                                        value={bonus}
                                        onChange={(e) => setBonus(e.target.value)}
                                        disabled={readOnly}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="eval-remarks">Remarks (optional)</Label>
                                <textarea
                                    id="eval-remarks"
                                    className="w-full rounded-md border border-input bg-background text-foreground px-3 py-2 text-sm shadow-sm"
                                    rows={3}
                                    value={remarks}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRemarks(e.target.value)}
                                    disabled={readOnly}
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter className="mt-4">
                        <Button variant="outline" size="sm" onClick={closeReview} disabled={saving}>
                            Cancel
                        </Button>
                        {!readOnly && (
                            <Button size="sm" onClick={handleSave} disabled={saving || !activeScenarioId}>
                                {saving ? 'Saving...' : 'Save evaluation'}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
